import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LuMoon, LuSun } from 'react-icons/lu';
import { HorizontalLine, HoverButton } from '../components';
import { profileData } from '../data';
import './../index.css';

let smoothScrollInProgress = false;

const HamburgerIcon = ({ isOpen, toggleMenu }) => (
  <button
    onClick={toggleMenu}
    type="button"
    className="rounded-md text-primary md:hidden focus:outline-none"
    aria-label="Toggle Navigation"
    aria-expanded={isOpen}
  >
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {isOpen ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M6 18L18 6M6 6l12 12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 6h16M4 12h16M4 18h16"
        />
      )}
    </svg>
  </button>
);

const getInitialDarkMode = () => {
  const savedTheme = window.localStorage.getItem('theme');

  if (savedTheme) return savedTheme === 'dark';

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const ThemeToggle = ({ isDarkMode, toggleTheme }) => (
  <button
    type="button"
    onClick={toggleTheme}
    className="flex items-center p-2 rounded-md text-primary focus:outline-none focus:ring-2 focus:ring-primary md:focus:ring-0"
    aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
  >
    {isDarkMode ? (
      <LuSun className="w-5 h-5 duration-300 hover:text-orange-400" />
    ) : (
      <LuMoon className="w-5 h-5 duration-300 hover:text-blue-800" />
    )}
  </button>
);

const NavButton = ({ title, href, onClick }) => {
  const handleClick = (event) => {
    onClick();

    if (smoothScrollInProgress) {
      event.preventDefault();
      return;
    }

    const currentPath = window.location.pathname;
    const isHomePath = currentPath === '/' || currentPath === '/home';

    if (href === '/home' && isHomePath) {
      event.preventDefault();
      smoothScrollInProgress = true;
      window.history.replaceState(null, '', '/');
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      window.setTimeout(() => {
        smoothScrollInProgress = false;
      }, 800);
      return;
    }

    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return;

    const targetId = decodeURIComponent(href.slice(hashIndex + 1));
    const target = document.getElementById(targetId);
    const targetPath = href.slice(0, hashIndex) || currentPath;
    const isSamePage =
      targetPath === currentPath ||
      (targetPath === '/' && currentPath === '/home') ||
      (targetPath === '/home' && currentPath === '/');

    if (target && isSamePage) {
      event.preventDefault();
      smoothScrollInProgress = true;
      target.scrollIntoView({ behavior: 'smooth' });
      const hashPath = isHomePath ? '/' : currentPath;
      window.history.replaceState(null, '', `${hashPath}#${targetId}`);
      window.setTimeout(() => {
        smoothScrollInProgress = false;
      }, 800);
    }
  };

  return (
    <NavLink
      to={href}
      onClick={handleClick}
      className="w-full outline-none focus:outline-none hover:text-primary md:w-auto"
    >
      <HoverButton className="w-full py-3 md:px-5 md:py-0">
        {title}
      </HoverButton>
    </NavLink>
  );
};

const MobileNavbar = ({
  isOpen,
  isDarkMode,
  toggleMenu,
  toggleTheme,
  closeMenu,
}) => (
  <nav
    className="text-primary bg-navbar backdrop-blur-lg md:hidden"
    role="navigation"
    aria-label="main navigation"
  >
    <div className="flex flex-row items-center justify-between px-8 py-5 text-xl font-semibold sm:px-16 border-vertical layout">
      <NavLink
        to="/home"
        onClick={closeMenu}
        className="uppercase hover:text-primary focus:outline-none"
      >
        {profileData.profile.full_name}
      </NavLink>
      <div className="flex items-center gap-x-5">
        <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
        <HamburgerIcon isOpen={isOpen} toggleMenu={toggleMenu} />
      </div>
    </div>
    <HorizontalLine />
    <div
      className={`${
        isOpen ? 'flex' : 'hidden'
      } flex-col items-start text-md font-medium border-vertical layout`}
    >
      <div className='w-full max-w-md px-8 py-3 mx-auto sm:px-0'>
        <NavButton title="Home" href="/home" onClick={closeMenu} />
        <NavButton title="Projects" href="/#projects" onClick={closeMenu} />
        <NavButton title="Contact" href="/#contact" onClick={closeMenu} />
      </div>
    </div>
    <HorizontalLine className={`${isOpen ? 'flex' : 'hidden'}`} />
  </nav>
);

const DesktopNavbar = ({ isDarkMode, toggleTheme, closeMenu }) => (
  <nav
    className="hidden border-b text-primary bg-navbar backdrop-blur-lg md:block border-foreground"
    role="navigation"
    aria-label="main navigation"
  >
    <div className="layout section-padding md:justify-between md:flex md:flex-nowrap border-vertical">
      <div className="flex flex-row items-center text-xl font-semibold layout border-vertical md:border-none">
        <NavLink
          to="/home"
          onClick={closeMenu}
          className="uppercase hover:text-primary focus:outline-none"
        >
          {profileData.profile.full_name}
        </NavLink>
      </div>
      <div className="flex flex-row items-center w-auto py-3 font-medium text-md">
        <NavButton title="Home" href="/home" onClick={closeMenu} />
        <NavButton title="Projects" href="/#projects" onClick={closeMenu} />
        <NavButton title="Contact" href="/#contact" onClick={closeMenu} />
        <div>
          <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
        </div>
      </div>
    </div>
  </nav>
);

const NavigationBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    window.localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);
  const toggleTheme = () => {
    document.documentElement.classList.add('theme-transition');
    setIsDarkMode((prev) => !prev);

    window.setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 300);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <MobileNavbar
        isOpen={isOpen}
        isDarkMode={isDarkMode}
        toggleMenu={toggleMenu}
        toggleTheme={toggleTheme}
        closeMenu={closeMenu}
      />
      <DesktopNavbar
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        closeMenu={closeMenu}
      />
    </header>
  );
};

export default NavigationBar;
